import { useState } from 'react';
import dayjs from 'dayjs';
import { useEffectAsync } from '../reactHelper';
import usePositionAttributes from '../common/attributes/usePositionAttributes';
import { formatDistance, formatSpeed } from '../common/util/formatter';
import { useAttributePreference } from '../common/util/preferences';
import { useTranslation } from '../common/components/LocalizationProvider';
import { prefixString } from '../common/util/stringUtils';
import PageLayout from '../common/components/PageLayout';
import SettingsMenu from './components/SettingsMenu';
import CollectionFab from './components/CollectionFab';
import CollectionActions from './components/CollectionActions';
import useSettingsStyles from './common/useSettingsStyles';
import DataTable from '../common/components/Datatable';
import fetchOrThrow from '../common/util/fetchOrThrow';

const MaintenacesPage = () => {
  const { classes } = useSettingsStyles();
  const t = useTranslation();

  const positionAttributes = usePositionAttributes(t);

  const [timestamp, setTimestamp] = useState(Date.now());
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const speedUnit = useAttributePreference('speedUnit');
  const distanceUnit = useAttributePreference('distanceUnit');

  useEffectAsync(async () => {
    setLoading(true);
    try {
      const response = await fetchOrThrow('/api/maintenance');
      setItems(await response.json());
    } finally {
      setLoading(false);
    }
  }, [timestamp]);

  const convertAttribute = (key, start, value) => {
    const attribute = positionAttributes[key];
    if (key.endsWith('Time')) {
      if (start) {
        return dayjs(value).locale('en').format('DD-MM-YYYY');
      }
      return `${value / 86400000} ${t('sharedDays')}`;
    }
    if (attribute && attribute.dataType) {
      switch (attribute.dataType) {
        case 'speed':
          return formatSpeed(value, speedUnit, t);
        case 'distance':
          return formatDistance(value, distanceUnit, t);
        case 'hours':
          return `${value / 3600000} ${t('sharedHours')}`;
        default:
          return value;
      }
    }

    return value;
  };

  const columns = [
    { field: 'id', headerName: 'ID', width: 80, disableColumnMenu: true },
    { field: 'name', headerName: t('sharedName'), flex: 1, disableColumnMenu: true },
    {
      field: 'type',
      headerName: t('sharedType'),
      flex: 1,
      disableColumnMenu: true,
      renderCell: (params) => {
        if (!params || !params.value) return '';


        const attribute = positionAttributes[params.value];
        if (attribute && attribute.name) {
          return attribute.name;
        }

        return params.value;
      },
    },
    {
      field: 'start',
      headerName: t('maintenanceStart'),
      flex: 1,
      disableColumnMenu: true,
      renderCell: (params) => {
        if (!params.row || !params.row.type || params.row.start === undefined) return '';
        return convertAttribute(params.row.type, true, params.row.start);
      },
    },
    {
      field: 'period',
      headerName: t('maintenancePeriod'),
      flex: 1,
      disableColumnMenu: true,
      renderCell: (params) => {
        if (!params.row || !params.row.type || params.row.period === undefined) return '';
        return convertAttribute(params.row.type, false, params.row.period);
      },
    },
    {
      field: 'actions',
      headerName: t('actions'),
      sortable: false,
      renderCell: (params) => (
        <div>
          <CollectionActions
            itemId={params.row.id}
            editPath="/settings/maintenance"
            endpoint="maintenance"
            setTimestamp={setTimestamp} />
        </div>
      ),
      width: 150,
      disableColumnMenu: true,
    }
  ];

  return (
    <PageLayout menu={<SettingsMenu />} breadcrumbs={['settingsTitle', 'sharedMaintenance']}>
      <DataTable
        tableName='maintenances'
        columns={columns}
        rows={items}
        loading={loading}
        getRowId={(row) => row.id}
      />
      <CollectionFab editPath="/settings/maintenance" />
    </PageLayout>
  );
};

export default MaintenacesPage;
